/**
 * 股票实时行情：订阅引用计数 + 订阅侧节流拉取。
 * HTTP 仅作首屏兜底（订阅变化时拉取一次，不轮询）；实时更新由 WS 推送（stock 模块）负责。
 * @module store/modules/stockRealtime
 */
import { queryStockRealtime } from '@/jmHttp/api/marketStock';

/** 订阅/退订风暴时，限制订阅触发的 HTTP 拉取最小间隔（毫秒） */
export const SUBSCRIBE_FETCH_THROTTLE_MS = 300;

function normalizeFullCodes(fullCodes) {
  if (!Array.isArray(fullCodes)) return [];
  const seen = new Set();
  const out = [];
  for (const raw of fullCodes) {
    const c = String(raw || '').trim();
    if (!c || seen.has(c)) continue;
    seen.add(c);
    out.push(c);
  }
  return out;
}

let subscribeMicrotaskQueued = false;
/** @type {ReturnType<typeof setTimeout> | null} */
let subscribeThrottleTimer = null;
/** @type {number} 上次由订阅/变更触发的拉取时间戳；0 表示尚未发生过 */
let lastSubscribeDrivenFetchAt = 0;

/**
 * @param {Function} dispatch namespaced dispatch
 * @param {Object} getters module getters
 */
function scheduleSubscribeDrivenFetch(dispatch, getters) {
  if (subscribeMicrotaskQueued) return;
  subscribeMicrotaskQueued = true;
  queueMicrotask(() => {
    subscribeMicrotaskQueued = false;
    if (!getters.activeFullCodes.length) return;

    const run = () => {
      lastSubscribeDrivenFetchAt = Date.now();
      if (subscribeThrottleTimer) {
        clearTimeout(subscribeThrottleTimer);
        subscribeThrottleTimer = null;
      }
      dispatch('fetchQuotes', { source: 'subscribe' });
    };

    const now = Date.now();
    const elapsed = now - lastSubscribeDrivenFetchAt;
    if (lastSubscribeDrivenFetchAt === 0 || elapsed >= SUBSCRIBE_FETCH_THROTTLE_MS) {
      run();
    } else {
      if (subscribeThrottleTimer) clearTimeout(subscribeThrottleTimer);
      subscribeThrottleTimer = setTimeout(run, SUBSCRIBE_FETCH_THROTTLE_MS - elapsed);
    }
  });
}

export default {
  namespaced: true,
  state: {
    /** @type {Record<string, number>} fullCode -> 引用计数 */
    refCounts: {},
    /** @type {Record<string, unknown>} fullCode -> 行情快照（退订后保留，避免 UI 闪空） */
    quotesByFullCode: {},
    fetchInFlight: false,
    pendingRefetch: false,
    lastError: null
  },
  getters: {
    activeFullCodes: (state) =>
      Object.entries(state.refCounts)
        .filter(([, n]) => n > 0)
        .map(([code]) => code),
    quoteByFullCode: (state) => {
      return (fullCode) => {
        const key = String(fullCode || '').trim();
        return key ? state.quotesByFullCode[key] ?? null : null;
      };
    },
    quotesForFullCodes: (state, getters) => (fullCodes) => {
      const list = Array.isArray(fullCodes) ? fullCodes : [];
      const pick = getters.quoteByFullCode;
      return list.map((code) => ({ fullCode: code, quote: pick(code) }));
    }
  },
  mutations: {
    applySubscribe(state, fullCodes) {
      for (const code of fullCodes) {
        state.refCounts[code] = (state.refCounts[code] || 0) + 1;
      }
    },
    applyUnsubscribe(state, fullCodes) {
      for (const code of fullCodes) {
        const n = (state.refCounts[code] || 0) - 1;
        if (n <= 0) {
          delete state.refCounts[code];
        } else {
          state.refCounts[code] = n;
        }
      }
    },
    mergeQuotes(state, payload) {
      if (!payload || typeof payload !== 'object') return;
      Object.assign(state.quotesByFullCode, payload);
    },
    setFetchInFlight(state, v) {
      state.fetchInFlight = v;
    },
    setPendingRefetch(state, v) {
      state.pendingRefetch = v;
    },
    setLastError(state, msg) {
      state.lastError = msg;
    }
  },
  actions: {
    subscribe({ commit, dispatch, getters }, { fullCodes }) {
      const codes = normalizeFullCodes(fullCodes);
      if (!codes.length) return;
      commit('applySubscribe', codes);
      // 订阅即触发一次首屏拉取（HTTP 兜底），后续涨跌幅更新由 WS 推送驱动
      scheduleSubscribeDrivenFetch(dispatch, getters);
    },
    unsubscribe({ commit }, { fullCodes }) {
      const codes = normalizeFullCodes(fullCodes);
      if (!codes.length) return;
      commit('applyUnsubscribe', codes);
    },
    async fetchQuotes({ commit, state, getters, dispatch }, payload = {}) {
      const { source = 'subscribe' } = payload;
      const codes = getters.activeFullCodes;
      if (!codes.length) {
        commit('setPendingRefetch', false);
        return;
      }

      if (state.fetchInFlight) {
        commit('setPendingRefetch', true);
        return;
      }

      commit('setFetchInFlight', true);
      if (source !== 'retry') {
        commit('setLastError', null);
      }
      try {
        const res = await queryStockRealtime({ fullCodes: codes });
        if (+res.code !== 0) return;
        const batch = {};
        res.data.forEach((item) => {
          batch[item.fullCode] = item.changePct;
        });
        commit('mergeQuotes', batch);
      } catch (e) {
        commit('setLastError', e?.message || String(e));
        console.error('[stockRealtime] fetchQuotes failed', e);
      } finally {
        commit('setFetchInFlight', false);
        const needRetry = state.pendingRefetch;
        if (needRetry) {
          commit('setPendingRefetch', false);
          dispatch('fetchQuotes', { source: 'retry' });
        }
      }
    }
  }
};
